<script setup lang="ts">
import { computed, nextTick, onMounted, ref, useTemplateRef, watch, type UnwrapRef } from 'vue'
import dayjs from 'dayjs'

const LANGUAGES = {
  eng: 'EN',
  rus: 'RU',
  ukr: 'UA',
  tat: 'TT'
}

const props = defineProps({
  studentId: Number,
  material: Object,
  color: {
    type: String,
    default: 'orange',
    validator: (value: string) => ['orange', 'blue'].includes(value)
  }
})

const emit = defineEmits(['play', 'ended', 'pause'])

const audio = ref<HTMLAudioElement | null>(null)
const range = ref<HTMLInputElement | null>(null)

const currentTime = ref(0)
const duration = ref(0)
const paused = ref(true)
const loop = ref(false)
const speed = ref(1)
const pickedLanguage = ref('rus')
const speeds = [0.25, 0.5, 0.75, 1, 1.5, 1.75, 2]
const todayListenCount = ref<number | null>(null)
const updateFrameId = ref<number | null>(null)

const activeMaterial = computed(() => props.material?.[activeLanguage.value])
const activeLanguage = computed(() => pickedLanguage.value || availableLanguages.value[0])
const storedLang = computed(() => localStorage.getItem(`_audio_${props.material?.id}_lang`))
const nonRusLang = computed(() => availableLanguages.value.find(language => language !== 'rus'))
const availableLanguages = computed(() =>
  (Object.keys(LANGUAGES) as Array<keyof typeof LANGUAGES>).filter(language => props.material?.[language])
)
const audioName = computed(() => {
  const name = `${props.material?.name}_${activeLanguage.value}.mp3`
  return name.replace(/\s/g, '_')
})

function formatTime(val: number) {
  const time = Math.ceil(val ?? 0)
  const min = Math.floor(time / 60)
    .toString()
    .padStart(2, '0')
  const sec = Math.floor(time % 60)
    .toString()
    .padStart(2, '0')
  return [min, sec].join(':')
}

function updateRangeBackground() {
  const element = range.value
  if (!element) return

  const ratio = ((element.valueAsNumber - Number(element.min)) / (Number(element.max) - Number(element.min))) * 100 || 0
  element.style.background = `linear-gradient(90deg, var(--active_color) ${ratio}%, var(--inactive_color) ${ratio}%)`
}

function onRangeChange(event: Event) {
  updateRangeBackground()
  if (!audio.value) return
  audio.value.currentTime = Number((event.currentTarget as HTMLInputElement).value)
}

function seek(amount: number) {
  if (!audio.value) return
  audio.value.currentTime = audio.value.currentTime + amount
}

function setSpeed(value: number) {
  if (!audio.value) return
  audio.value.playbackRate = value
  speed.value = value
}

function update() {
  if (!audio.value) return

  const { paused: isPaused, currentTime: time, duration: totalDuration, playbackRate } = audio.value

  if (paused.value !== isPaused) paused.value = isPaused
  if (currentTime.value !== time) currentTime.value = time
  if (duration.value !== totalDuration) duration.value = totalDuration
  if (speed.value !== playbackRate) {
    if (speeds.includes(playbackRate)) speed.value = playbackRate
    else audio.value.playbackRate = speed.value
  }

  if (!isPaused) {
    updateFrameId.value = requestAnimationFrame(update)
  }
}

function onLoadedMetadata() {
  if (!audio.value) return
  duration.value = audio.value.duration
}

watch(
  paused,
  async value => {
    if (!audio.value) return

    document.querySelectorAll('[data-vue-audio-player] audio').forEach(item => (item as HTMLAudioElement).pause())

    if (value) {
      audio.value.pause()
      if (updateFrameId.value !== null) {
        cancelAnimationFrame(updateFrameId.value)
        updateFrameId.value = null
      }
    } else {
      await audio.value.play()
      update()
      emit('play')
    }

    if (value) return
    if (audio.value.currentTime === 0 && !storedLang.value && availableLanguages.value.length > 1) {
      todayListenCount.value = (todayListenCount.value ?? 0) + 1
      pickedLanguage.value = (todayListenCount.value ?? 0) > 3 ? nonRusLang.value || 'rus' : 'rus'
      update()
      await nextTick()
      await audio.value.play()
    }
  },
  { flush: 'post' }
)

watch(currentTime, () => {
  updateRangeBackground()
})

watch(pickedLanguage, value => {
  localStorage.setItem(`_audio_${props.material?.id}_lang`, value)
})

watch(todayListenCount, (value, oldValue) => {
  if (oldValue === null) return
  const stored = localStorage.getItem('studentListenHistory')
  const currentValue = JSON.parse(stored || '{}')

  currentValue[`${props.studentId}-${props.material?.id}`] = { updatedAt: Date.now(), listenCount: value }
  localStorage.setItem('studentListenHistory', JSON.stringify(currentValue))
})

const storedHistory = localStorage.getItem('studentListenHistory')
const listenHistoryEntry = JSON.parse(storedHistory || '{}')[`${props.studentId}-${props.material?.id}`]
if (listenHistoryEntry && dayjs(listenHistoryEntry.updatedAt).isSame(dayjs(), 'day')) {
  todayListenCount.value = listenHistoryEntry.listenCount
} else {
  todayListenCount.value = 0
}

onMounted(() => {
  if (todayListenCount.value === 0) localStorage.removeItem(`_audio_${props.material?.id}_lang`)

  const override = localStorage.getItem(`_audio_${props.material?.id}_lang`)
  if (override) {
    pickedLanguage.value = override
  } else if (availableLanguages.value.length > 1) {
    pickedLanguage.value = (todayListenCount.value ?? 0) > 3 ? nonRusLang.value || 'rus' : 'rus'
  }

  updateRangeBackground()
})

const root = useTemplateRef('root')

export interface AudioPlayerV2Expose {
  $el: UnwrapRef<typeof root>
  play(): void
}

defineExpose<AudioPlayerV2Expose>({
  $el: root,
  play: () => {
    paused.value = false
  }
} as unknown as AudioPlayerV2Expose) // A Vue quirk :(
</script>

<template>
  <div
    ref="root"
    class="student-v2-audio-player"
    :class="`student-v2-audio-player_color_${color}`"
    data-vue-audio-player
  >
    <audio
      ref="audio"
      :key="activeMaterial.url"
      preload="metadata"
      :loop="loop"
      :data-active-material-id="activeMaterial.id"
      @loadedmetadata="onLoadedMetadata"
      @ended="emit('ended', $event)"
      @pause="emit('pause', $event)"
    >
      <source :src="activeMaterial.url" type="audio/mp3" />
    </audio>

    <div class="student-v2-audio-player__controls-row">
      <button
        class="student-v2-audio-player__icon-btn student-v2-audio-player__playpause-btn"
        @click="paused = !paused"
      >
        <v-icon v-if="paused" key="0">mdi-play</v-icon>
        <v-icon v-else key="1">mdi-pause</v-icon>
      </button>

      <p class="text-title flex-spacer">
        {{ formatTime(currentTime) }}
        <span v-if="duration && !isNaN(duration)" class="gray-text"> / {{ formatTime(duration) }} </span>
      </p>

      <slot name="controls">
        <v-menu-base no-mobile-modal>
          <template #activator="{ onclick }">
            <v-link class="text-body-bold" @click="onclick"> {{ speed }}x </v-link>
          </template>

          <ul class="pa-1">
            <v-menu-button v-for="s in speeds" :key="s" @click="setSpeed(s)">
              {{ s === 1 ? 'Обычная' : `${s}x` }}
              <template v-if="speed === s" #append>
                <v-icon class="green-text ml-1">mdi-check</v-icon>
              </template>
            </v-menu-button>
          </ul>
        </v-menu-base>

        <v-link @click="loop = !loop">
          <v-icon :class="loop ? 'orange-text' : 'gray-text'" size="28">mdi-repeat</v-icon>
        </v-link>

        <ul v-if="availableLanguages.length > 1" class="student-v2-audio-player__btn-group">
          <li v-for="lang in availableLanguages" :key="lang">
            <button
              class="student-v2-audio-player__btn-in-group"
              :class="{ 'student-v2-audio-player__btn-in-group_active': activeLanguage === lang }"
              @click="pickedLanguage = lang"
              v-text="LANGUAGES[lang]"
            />
          </li>
        </ul>

        <a
          class="student-v2-audio-player__icon-btn student-v2-audio-player__icon-btn_small"
          :href="activeMaterial.url + '?download=true'"
          :download="audioName"
        >
          <v-icon>mdi-download</v-icon>
        </a>

        <slot name="controls-append" />
      </slot>
    </div>

    <div class="student-v2-audio-player__controls-row">
      <v-link @click="seek(-5)">
        <v-icon class="student-v2-audio-player__colored-icon">mdi-rewind-5</v-icon>
      </v-link>

      <input
        ref="range"
        type="range"
        class="student-v2-audio-player__input-range flex-spacer"
        :value="currentTime"
        :max="duration"
        @input="onRangeChange"
      />

      <v-link @click="seek(5)">
        <v-icon class="student-v2-audio-player__colored-icon">mdi-fast-forward-5</v-icon>
      </v-link>
    </div>
  </div>
</template>

<style lang="scss">
@use '~/styles/pages/student2/screen' as s2;

.student-v2-audio-player {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;

  border-radius: 24px;
  border: 1px solid var(--active_color);

  &_color {
    &_orange {
      --active_color: #{$orange};
      --inactive_color: #{$lightenOrange};
    }

    &_blue {
      --active_color: #{$blue};
      --inactive_color: #{$lightenBlue};
    }
  }

  &__controls-row {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  &__icon-btn {
    background: var(--active_color);
    color: white;
    width: 56px;
    height: 56px;
    border-radius: 999px;

    display: flex;
    justify-content: center;
    align-items: center;

    cursor: pointer;

    &_small {
      width: 48px;
      height: 48px;
    }

    @include s2.mobile {
      width: 48px;
      height: 48px;

      &_small {
        width: 40px;
        height: 40px;
      }
    }
  }

  &__btn-group {
    display: flex;
    background: var(--inactive_color);
    height: 48px;
    border-radius: 999px;

    display: flex;

    @include s2.mobile {
      height: 40px;
    }
  }

  &__btn-in-group {
    color: white;
    width: 48px;
    height: 48px;
    border-radius: 999px;

    display: flex;
    justify-content: center;
    align-items: center;

    cursor: pointer;
    font-weight: bold;

    @include s2.mobile {
      width: 40px;
      height: 40px;
    }

    &_active {
      background: var(--active_color);
    }
  }

  &__colored-icon {
    color: var(--active_color);
  }

  &__input-range {
    appearance: none;
    width: 200px;
    height: 24px;
    border-radius: 9999px;
    background: linear-gradient(90deg, var(--active_color) 0%, var(--inactive_color) 0%);
    cursor: pointer;

    &::-webkit-slider-thumb {
      -webkit-appearance: none;
      appearance: none;
      width: 0px;
      height: 0px;
    }

    &::-moz-range-thumb {
      border: none;
      width: 0px;
      height: 0px;
    }
  }
}
</style>
